/**
 * Parwaaz backend — pluggable e-mail delivery.
 * 'mailapp' delegates to platform.sendMail (Apps Script's MailApp in production, console+outbox
 * in the dev/memory platform). 'brevo' calls the Brevo transactional API directly when the
 * platform exposes a `fetch`. 'none' is a silent no-op (used in tests that don't care about mail).
 */
import type { Platform } from '../store';

export interface Mailer {
  send(to: string, subject: string, html: string, text?: string): void;
}

export type MailProvider = 'mailapp' | 'brevo' | 'none';

export interface MailerOpts {
  fromName: string;
  fromAddress: string;
  brevoApiKey?: string;
}

/** Platforms may optionally expose a fetch for outbound HTTP (Node/browser do; Apps Script doesn't). */
type FetchCapablePlatform = Platform & { fetch?: typeof fetch };

export function createMailer(provider: MailProvider, platform: Platform, opts: MailerOpts): Mailer {
  if (provider === 'none') {
    return { send: () => {} };
  }

  if (provider === 'mailapp') {
    return {
      send: (to, subject, html, text) => platform.sendMail(to, subject, html, text),
    };
  }

  // brevo
  const fetchFn = (platform as FetchCapablePlatform).fetch;
  if (!fetchFn) {
    throw new Error('brevo mail provider is not available in this runtime');
  }
  return {
    send: (to, subject, html, text) => {
      fetchFn('https://api.brevo.com/v3/smtp/email', {
        method: 'POST',
        headers: { 'content-type': 'application/json', 'api-key': opts.brevoApiKey ?? '' },
        body: JSON.stringify({
          sender: { name: opts.fromName, email: opts.fromAddress },
          to: [{ email: to }],
          subject,
          htmlContent: html,
          textContent: text ?? '',
        }),
      }).catch((err) => platform.log('brevo send failed', err));
    },
  };
}
